import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from './client';

export { ApiError };

export const PAGE_SIZE = 25;

// ---------- Interfaces ----------

export interface PlatformUserMembership {
  id: string;
  organisation_id: string;
  organisation_name: string | null;
  role: { id: string; name: string };
  product_access?: Array<{ product_id: string }>;
}

export interface PlatformUser {
  id: string;
  external_id: string;
  email: string;
  display_name: string;
  user_type: 'clinician' | 'admin' | 'patient' | 'user' | 'documentation';
  status: 'active' | 'suspended' | 'deactivated';
  created_at: string;
  updated_at: string;
  practitioner?: {
    professional_id: string | null;
    professional_id_type: string | null;
    speciality: string | null;
    credentials: string | null;
  };
  patient?: { clinical_patient_id: string | null; contact_preferences: unknown };
  memberships: PlatformUserMembership[];
}

export interface PlatformUserListEnvelope {
  items: PlatformUser[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListFilters {
  org?: string;
  type?: 'clinician' | 'patient' | 'user' | 'documentation';
  status?: 'active' | 'deactivated';
  search?: string;
  productId?: string;
  page: number; // 1-based
}

export interface RoleSummary {
  id: string;
  organisation_id: string;
  name: string;
  description: string | null;
  is_default: boolean;
}

export interface CreatePlatformUserInput {
  email: string;
  display_name: string;
  user_type: 'clinician' | 'patient' | 'user' | 'documentation';
  organisation_id?: string;
  role_id?: string;
  professional_id?: string;
  professional_id_type?: string;
  speciality?: string;
  credentials?: string;
}

// ---------- Query keys ----------

const KEYS = {
  list: (filters: ListFilters) => ['platform-users', 'list', filters] as const,
  detail: (id: string) => ['platform-users', 'detail', id] as const,
  roles: (orgId: string) => ['roles', orgId] as const,
};

// ---------- Hooks ----------

export function usePlatformUsers(filters: ListFilters | null) {
  return useQuery({
    queryKey: filters ? KEYS.list(filters) : ['platform-users', 'list', null],
    queryFn: () => {
      if (!filters) return Promise.reject(new Error('No filters'));
      const offset = (filters.page - 1) * PAGE_SIZE;
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (filters.org) params.set('org', filters.org);
      if (filters.type) params.set('type', filters.type);
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      if (filters.productId) params.set('product_id', filters.productId);
      return apiGet<PlatformUserListEnvelope>(`/api/platform-users?${params.toString()}`);
    },
    enabled: filters !== null,
    placeholderData: keepPreviousData,
  });
}

export function usePlatformUser(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () => apiGet<PlatformUser>(`/api/platform-users/${id}`),
    enabled: !!id,
  });
}

export function useRolesForOrg(orgId: string | undefined) {
  return useQuery({
    queryKey: KEYS.roles(orgId ?? ''),
    queryFn: () => apiGet<RoleSummary[]>(`/api/roles?org=${orgId}`),
    enabled: !!orgId,
    staleTime: 60_000,
  });
}

export function useCreatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePlatformUserInput) =>
      apiPost<PlatformUser>('/api/platform-users', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-users', 'list'] });
    },
  });
}

export interface UpdatePlatformUserInput {
  display_name?: string;
  professional_id?: string;
  professional_id_type?: string;
  speciality?: string;
  credentials?: string;
}

export function useUpdatePlatformUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePlatformUserInput) =>
      apiPatch<PlatformUser>(`/api/platform-users/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-users'] }),
  });
}

export function useDeactivatePlatformUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/api/platform-users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-users', 'list'] });
    },
  });
}

// ---------- Clinical-patient hooks ----------
// Surface clinical-api Patient rows alongside platform users on the
// Users page. The /api/platform-users/clinical-patients endpoint
// proxies clinical-api's admin-patients listing scoped to a single
// org. Records here have NO platform-user identity until the admin
// runs `promoteClinicalPatient`.
export interface ClinicalPatientRowProduct {
  id: string;
  code: string;
  display_name: string;
}

export interface ClinicalPatientRow {
  id: string;
  organisation_id: string;
  given_name: string;
  family_name: string;
  dob: string;
  email: string | null;
  status: string;
  case_count: number;
  // Distinct products linked to this patient via their cases. Empty
  // when the patient has no cases yet (rare — patients are usually
  // created at case-creation time so a patient with 0 cases is an
  // edge case).
  products: ClinicalPatientRowProduct[];
  created_at: string;
}

export interface ClinicalPatientListEnvelope {
  items: ClinicalPatientRow[];
  total: number;
  grand_total: number;
  limit: number;
  offset: number;
}

export function useClinicalPatients(
  filters: { org?: string; search?: string; page: number } | null,
) {
  return useQuery({
    queryKey: ['clinical-patients', 'list', filters],
    queryFn: () => {
      if (!filters) return Promise.reject(new Error('No filters'));
      const offset = (filters.page - 1) * PAGE_SIZE;
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (filters.org) params.set('org', filters.org);
      if (filters.search) params.set('search', filters.search);
      return apiGet<ClinicalPatientListEnvelope>(
        `/api/platform-users/clinical-patients?${params.toString()}`,
      );
    },
    enabled: filters !== null,
  });
}

export interface PromoteClinicalPatientInput {
  clinical_patient_id: string;
  organisation_id: string;
  email: string;
  display_name: string;
  role_id: string;
}

export function usePromoteClinicalPatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PromoteClinicalPatientInput) =>
      apiPost<PlatformUser>('/api/platform-users/promote-clinical-patient', input),
    onSuccess: () => {
      // Both lists need a refresh — the new platform user appears and
      // the clinical-patient row will now be filtered out client-side
      // by the dedup logic.
      qc.invalidateQueries({ queryKey: ['platform-users', 'list'] });
      qc.invalidateQueries({ queryKey: ['clinical-patients', 'list'] });
    },
  });
}

export function useReactivatePlatformUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<PlatformUser>(`/api/platform-users/${id}/reactivate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-users', 'list'] });
    },
  });
}

export function useResetPlatformUserPassword(id: string) {
  return useMutation({
    mutationFn: () => apiPost<void>(`/api/platform-users/${id}/reset-password`),
  });
}

export interface AddMembershipInput {
  organisation_id: string;
  role_id: string;
}

export function useAddPlatformUserMembership(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddMembershipInput) =>
      apiPost<PlatformUserMembership>(`/api/platform-users/${userId}/memberships`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-users'] }),
  });
}

export function useUpdatePlatformUserMembershipRole(userId: string, membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { role_id: string }) =>
      apiPatch<PlatformUserMembership>(
        `/api/platform-users/${userId}/memberships/${membershipId}`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-users'] }),
  });
}

export function useRemovePlatformUserMembership(userId: string, membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`/api/platform-users/${userId}/memberships/${membershipId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-users'] }),
  });
}
